import React from 'react';
import { Link } from 'react-router-dom';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import withMobileDialog from '@material-ui/core/withMobileDialog';

import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';

import Api from '../../../../../../common/api';
import Util from '../../../../../../common/util';
import withTranslations from '../../../../../../common/components/with-translations';

const styles = theme => ({
    centerText: {
        textAlign: 'center',
    },
    dialogActions: {
        justifyContent: 'center',
        margin: '8px 4px 16px'
    },
    dialogBodyApplicationList: {
        marginTop: '0px'
    },
    dialogBodyText: {
        display: 'block',
        paddingBottom: '15px'
    },
    dialogContent: {
        paddingBottom: '0px'
    },
    dialogLink: {
        color: '#666',
        fontWeight: 'bold'
    },
    errorLink: {
        textDecoration: 'none',
        cursor: 'pointer',
        fontWeight: 'bold',
        color: '#FFF'
    },
    errorText: {
        margin: '0 0 10px',
        '&:last-of-type': {
            marginBottom: 0
        }
    }
});

class AuthInitiationRequestView extends React.Component {

    constructor(props) {

        super(props);

        this.state = {
            application: {},
            open: false,
            error: null
        };

        this.onApprove = this.onApprove.bind(this);
        this.onDeny = this.onDeny.bind(this);
        this.getApplication = this.getApplication.bind(this);
        this.runOauthFlow = this.runOauthFlow.bind(this);
        this.applyOauthResponses = this.applyOauthResponses.bind(this);
        this.api = new Api();
    }

    async onApprove() {
        const { t } = this.props;

        try {
            const response = await this.api.post(`/application/${this.props.query.client_id}/grant`);
            if (response.code !== 202 && response.code !== 304) {
                return this.props.onError({
                    error: (
                        <div>
                            <p className={this.props.classes.errorText}>{t('authInitiationRequestView.unableToUpdateSettingsError')}</p>
                            <p className={this.props.classes.errorText}>{t('authInitiationRequestView.contactSupport')}</p>
                            <p className={this.props.classes.errorText}>
                                {t('authInitiationRequestView.clickHereInfo', {
                                    link: (
                                        <a className={this.props.classes.errorLink} href="/">{t('authInitiationRequestView.clickHereLinkText')}</a>
                                    )
                                })}
                            </p>
                        </div>
                    )
                })
            }
        }
        catch (e) {
            return this.props.onError({ error: e.toString() });
        }

        const auth = await this.runOauthFlow();
        return this.applyOauthResponses(auth);
    }

    onDeny() {

        this.props.onDeny(
            this.state.application.applicationNames ?
            this.state.application.organizationName :
            this.state.application.applicationName
        );
    }

    async getApplication() {
        const { t } = this.props;
        try {
            const application = await this.api.get(`/application/${this.props.query.client_id}`);
            if (application.code !== 200) {
                if (application.code === 404) {
                    throw new Error(t('authInitiationRequestView.applicationNotFoundError', { clientId: this.props.query.client_id }));
                }
                throw new Error(t('authInitiationRequestView.genericFetchError'));
            }

            return application.content;
        }
        catch (e) {
            throw e;
        }
    }

    async runOauthFlow() {

        const granted = await this.api.get(`/application/token${Util.toCoreQueryString(this.props.query)}`);
        return {
            code: granted.code,
            context: await async function () {

                switch (granted.code) {
                    case 200: return { granted: granted.content };
                    case 401: return { logout: true };
                    case 403: return { application: await this.getApplication() };
                    case 428: return { reconcile: true };
                    default: return { code: granted.code };
                }
            }.bind(this)()
        };
    }

    applyOauthResponses(results) {
        const { t } = this.props;

        if (results.context.logout) {
            return this.props.onLogout();
        }
        else if (results.context.reconcile) {
            return this.props.onRequiresReconciliation();
        }
        else if (results.context.application) {
            if (this.props.pixel)
              return this.props.onApprove(null);

            return this.setState({ open: true, application: results.context.application });
        }
        else if (results.context.granted) {
            return this.props.onApprove(results.context.granted);
        }
        else {
            return this.props.onError({
                error: (
                    <div>
                        <p className={this.props.classes.errorText}>{t('authInitiationRequestView.misconfiguredApplicationError')}</p>
                        <p className={this.props.classes.errorText}>{t('authInitiationRequestView.contactSupport')}</p>
                        <p className={this.props.classes.errorText}>
                            {t('authInitiationRequestView.clickHereInfo', {
                                link: (
                                    <a className={this.props.classes.errorLink} href="/">{t('authInitiationRequestView.clickHereLinkText')}</a>
                                )
                            })}
                        </p>
                    </div>
                ),
                critical: true
            });
        }
    }

    async componentDidMount() {

        if (this.props.open) {
            try {
                const results = await this.runOauthFlow();
                this.applyOauthResponses(results);
            }
            catch (e) {
                return this.props.onError({ error: e.stack || e.toString() });
            }
        }
    }

    render() {
        const { t } = this.props;
        const displayables = this.state.application.displayable ? this.state.application.displayable : {};
        const termsUrl = displayables.termsAndConditionsUrl ? displayables.termsAndConditionsUrl : null;
        const privacyUrl = displayables.privacyPolicyUrl ? displayables.privacyPolicyUrl : null;
        let applicationName = this.state.application.applicationName

        if (displayables.content && displayables.content.title)
          applicationName = displayables.content.title;

        let policyLinks = null;

        if (termsUrl !== null && privacyUrl !== null) {
          policyLinks = (
            <span className={this.props.classes.dialogBodyText + ' ' + this.props.classes.centerText}>
              {t('authInitiationRequestView.agreeToTerms', {
                termsLink: <a href={termsUrl}>{t('authInitiationRequestView.termsLinkText')}</a>,
                privacyLink: <a href={privacyUrl}>{t('authInitiationRequestView.privacyLinkText')}</a>
              })}
            </span>
          );
        }

        return (
            <Dialog
                disableEscapeKeyDown
                open={Object.keys(this.state.application).length > 0 && this.props.open}
            >
                {
                    this.state.application.applicationNames ?
                    (
                        <DialogTitle>
                            {t('authInitiationRequestView.authorizeMultipleTitle')}
                        </DialogTitle>
                    )
                    :
                    (
                        <DialogTitle>
                                {t('authInitiationRequestView.authorizeSingleTitle', { name: this.state.application.applicationName })}
                        </DialogTitle>
                    )
                }
                <DialogContent className={this.props.classes.dialogContent}>
                    {
                        this.state.application.applicationNames ?
                        (
                            <DialogContentText component="div">
                                <span className={this.props.classes.dialogBodyText}>
                                    {t('authInitiationRequestView.multipleAppsIntro', { orgName: this.state.application.organizationName })}
                                </span>
                                <ul className={this.props.classes.dialogBodyApplicationList}>
                                    {this.state.application.applicationNames.map((a) => {

                                        return (<li key={a}>{a}</li>)
                                    })}
                                </ul>
                                <span className={this.props.classes.dialogBodyText}>
                                    {t('authInitiationRequestView.managePreferencesInfo', {
                                        link: <Link className={this.props.classes.dialogLink} to="/preferences">{t('authInitiationRequestView.preferencesLinkText')}</Link>
                                    })}
                                </span>
                                {policyLinks}
                            </DialogContentText>
                        )
                        :
                        (
                            <DialogContentText>
                                <span className={this.props.classes.dialogBodyText}>
                                    {t('authInitiationRequestView.singleAppIntro', { appName: applicationName, orgName: this.state.application.organizationName })}
                                </span>
                                <span className={this.props.classes.dialogBodyText}>{t('authInitiationRequestView.singleAppAuthorizeInfo')}</span>
                                <span className={this.props.classes.dialogBodyText}>
                                    {t('authInitiationRequestView.managePreferencesInfo', {
                                        link: <Link className={this.props.classes.dialogLink} to="/preferences">{t('authInitiationRequestView.preferencesLinkText')}</Link>
                                    })}
                                </span>
                                {policyLinks}
                            </DialogContentText>
                        )
                    }
                </DialogContent>
                <DialogActions className={this.props.classes.dialogActions}>
                    <Button color="primary" onClick={this.onDeny} variant="contained">{t('authInitiationRequestView.declineButton')}</Button>
                    <Button color="primary" onClick={this.onApprove} variant="outlined">{t('authInitiationRequestView.approveButton')}</Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default withTranslations(withStyles(styles)(withMobileDialog()(AuthInitiationRequestView)));
